import Spinner from '../ui/spinner';
import { Globe, Lock, Users, Search, MoreVertical } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useListParams from '../../hooks/useListParams';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDate } from '../../utils/formatDate';
import { ISpaceWithStats } from '../../types';
import Pagination from '../Pagination';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface ISpaceListViewProps {
  spaces: ISpaceWithStats[];
  total: number;
  totalPages: number;
  isLoading: boolean;
  onViewDetail: (space: ISpaceWithStats) => void;
  onEdit: (space: ISpaceWithStats) => void;
  onViewMembers: (space: ISpaceWithStats) => void;
  onToggleStatus: (space: ISpaceWithStats) => void;
  onNavigateUser?: (userId: string) => void;
}

const SpaceListView = ({
  spaces,
  total,
  totalPages,
  isLoading,
  onViewDetail,
  onEdit,
  onViewMembers,
  onToggleStatus,
  onNavigateUser,
}: ISpaceListViewProps) => {
  const { t } = useTranslation();
  const { search, page, setSearch, setPage } = useListParams();

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {/* Search */}
      <Card className="p-4 gap-0">
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <Search className="w-5 h-5" />
            </span>
            <Input
              placeholder={t('spaces.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="SpaceManagement_List_searchInput"
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="gap-0 overflow-hidden">
        <Table style={{ minWidth: '960px' }}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('spaces.space_name')}</TableHead>
              <TableHead>{t('spaces.space_owner')}</TableHead>
              <TableHead>{t('spaces.type')}</TableHead>
              <TableHead>{t('spaces.member_count')}</TableHead>
              <TableHead>{t('spaces.rooms')}</TableHead>
              <TableHead>{t('common.created_at')}</TableHead>
              <TableHead className="text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spaces.map((space) => {
              const ownerName =
                space.owner?.displayName || space.owner?.username || '—';
              const ownerId = space.owner?.id || (space as any).ownerId;

              return (
                <TableRow
                  key={space.id}
                  onClick={() => onViewDetail(space)}
                  className="cursor-pointer"
                  data-testid={`SpaceManagement_List_spaceRow_${space.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center text-white overflow-hidden shrink-0">
                        {space.avatar ? (
                          <img
                            src={getImageUrl(space.avatar)!}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Globe className="w-5 h-5" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {space.name}
                        </div>
                        <div className="text-xs text-gray-400 max-w-[240px] truncate">
                          {space.description || '—'}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell
                    className="text-sm text-muted-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {ownerId && onNavigateUser ? (
                      <button
                        onClick={() => onNavigateUser(ownerId)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        data-testid={`SpaceManagement_List_ownerLink_${space.id}`}
                      >
                        {ownerName}
                      </button>
                    ) : (
                      ownerName
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        space.visibility === 'private' ? 'purple' : 'info'
                      }
                      className="inline-flex items-center"
                    >
                      {space.visibility === 'private' ? (
                        <Lock className="w-3 h-3 mr-1" />
                      ) : (
                        <Globe className="w-3 h-3 mr-1" />
                      )}
                      {space.visibility === 'private'
                        ? t('spaces.type_private')
                        : t('spaces.type_public')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    <Users className="w-4 h-4 inline text-muted-foreground" />{' '}
                    {space.memberCount ?? space.members?.length ?? 0}
                  </TableCell>
                  <TableCell className="text-sm text-foreground">
                    {space.roomCount ?? 0}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(space.createdAt)}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-foreground"
                          data-testid={`SpaceManagement_List_rowActionsButton_${space.id}`}
                        >
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="min-w-[160px]"
                      >
                        <DropdownMenuItem
                          onClick={() => onViewDetail(space)}
                          data-testid={`SpaceManagement_List_viewDetailButton_${space.id}`}
                        >
                          {t('spaces.view_detail')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onEdit(space)}
                          data-testid={`SpaceManagement_List_editInfoButton_${space.id}`}
                        >
                          {t('spaces.edit_info')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onViewMembers(space)}
                          data-testid={`SpaceManagement_List_viewMembersButton_${space.id}`}
                        >
                          {t('spaces.view_members')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onToggleStatus(space)}
                          variant="destructive"
                          data-testid={`SpaceManagement_List_deleteSpaceButton_${space.id}`}
                        >
                          {t('spaces.delete_space')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={20}
          onPageChange={setPage}
          testId="SpaceManagement_List"
        />
      </Card>
    </>
  );
};

export default SpaceListView;
